/* eslint complexity: ["error", {max: 9}] -- handleKeyPress */
/* eslint react/jsx-max-depth: ["error", {max: 5}] -- applies to entire file */
/* eslint max-lines: ["error", {max: 310}] -- many validations and handler functions inherent to the feature */
/* eslint max-lines-per-function: ["error", {max: 220, skipBlankLines: true, skipComments: true}] -- this applies to the entire file */
/* eslint max-statements: ["error", {max: 26}] -- this applies to the entire file */
import { useState } from "react";
import type { ComponentProps } from "react";

import isEmpty from "lodash/isEmpty";
import { Keyboard } from "react-native";
import uuid from "react-native-uuid";

import {
  ImageCompressionSource,
  MessagesAnalyticsEvent,
  MessagesAnalyticsKey,
} from "@/constants/analytics/Analytics";
import { AcceptedDocumentMimeTypes } from "@/constants/Documents";
import { ChannelCardTestID } from "@/constants/TestID";
import { InputFieldBaseStyles } from "@/constants/UI";
import type {
  GroupChannel,
  SendbirdInputProps,
} from "@/crossPlatform/Sendbird";
import { useMentionTextInput } from "@/hooks/useMentionTextInput";
import useWebSafeChatPatientId from "@/hooks/useWebSafeChatPatientId";
import { useAnalytics } from "@/services/Analytics";
import useAppMonitor from "@/services/AppMonitor";
import { useGetPatientDocumentUploadURLMutation } from "@/store/api/chat";
import { useUploadFileMutation } from "@/store/api/documents";
import { useAppUserSelector } from "@/store/reducers/appUser";
import { FrozenChannelBanner } from "@/ui/components/messages/fragments/GroupChannelBanners";
import MentionList from "@/ui/components/messages/fragments/MentionList";
import FilePickerModal from "@/ui/composed/pickers/FilePickerModal";
import { FAIconName, IconButton, IconButtonSize, TextField } from "@/ui/core";
import BaseDocumentCard from "@/ui/core/BaseDocumentCard";
import { Box, HStack, KeyboardAvoidingView, VStack } from "@/ui/layout";
import { shouldDisableSendButton } from "@/utils/messages.util";
import { isWeb } from "@/utils/platform.util";
import { convertAssetToFile } from "@/utils/shared.utils";
import { joinTestIDs } from "@/utils/string.util";

const baseTestID = "chat_channel_input";

interface ChannelInputProps extends Pick<
  SendbirdInputProps,
  "onPressSendFileMessage" | "onPressSendUserMessage"
> {
  channel: GroupChannel;
  channelDisplayName: string;
  isChannelFrozen: boolean;
  isGroupChannel: boolean;
  members: BaseUserAvatarProps[];
}

const ChannelInput = ({
  channel,
  channelDisplayName,
  isChannelFrozen,
  isGroupChannel,
  members,
  onPressSendFileMessage,
  onPressSendUserMessage,
}: ChannelInputProps) => {
  const appUser = useAppUserSelector();
  const { externalId: appUserId } = appUser;
  const filterCurrentMember = members.filter(
    (user) => user.userId !== appUserId,
  );

  const {
    getMentions,
    handleInputChange,
    handleMentionSelect,
    handleSelectionChange,
    mentionCandidates,
    mentionQuery,
    message,
    resetState,
  } = useMentionTextInput(filterCurrentMember);

  const { track } = useAnalytics();
  const { captureException } = useAppMonitor();
  const selectedPatientId = useWebSafeChatPatientId(channel);

  const [isAttachmentProcessingError, setIsAttachmentProcessingError] =
    useState<boolean>(false);

  const [selectedAttachment, setSelectedAttachment] =
    useState<FilePickerAsset | null>(null);

  const [uploadFileMutation, { reset: resetUploadFile }] =
    useUploadFileMutation();
  const [getUploadURL] = useGetPatientDocumentUploadURLMutation();

  const [isAttachmentProcessing, setIsAttachmentProcessing] =
    useState<boolean>(false);

  const [isImagePickerModalOpen, setIsImagePickerModalOpen] =
    useState<boolean>(false);

  const handleSendMessagePress = () => {
    if (selectedAttachment) {
      void handleSendFileMessagePress();
    }
    handleSendUserMessagePress();
    Keyboard.dismiss();
  };

  // on web, allow enter key to send message (shift+enter inserts a newline)
  const handleKeyPress: NonNullable<
    ComponentProps<typeof TextField>["onKeyPress"]
  > = (event) => {
    if (!isWeb()) return;
    const { nativeEvent } = event;
    const isShiftHeld =
      "shiftKey" in nativeEvent && Boolean(nativeEvent.shiftKey);
    // languages that use IME composition use Enter to confirm a composition selection
    // if user is mid-composition, do not register that Enter as a trigger to send the message
    const isComposing =
      "isComposing" in nativeEvent && Boolean(nativeEvent.isComposing);
    if (isShiftHeld || isComposing || nativeEvent.key !== "Enter") return;
    if ("preventDefault" in event) event.preventDefault();
    if (selectedAttachment) void handleSendFileMessagePress();
    handleSendUserMessagePress();
  };

  const handleSendUserMessagePress = () => {
    if (isEmpty(message)) {
      return;
    }

    const mentionedUserIds = getMentions();

    void onPressSendUserMessage({
      mentionedUserIds,
      message,
    });

    resetState();
    track(MessagesAnalyticsEvent.SendMessagePressed, {
      [MessagesAnalyticsKey.ChannelName]: channelDisplayName,
      [MessagesAnalyticsKey.IsGroupChannel]: isGroupChannel,
    });
  };

  const handleAttachmentPress = () => {
    track(MessagesAnalyticsEvent.AddAttachmentPressed, {
      [MessagesAnalyticsKey.ChannelName]: channelDisplayName,
      [MessagesAnalyticsKey.IsGroupChannel]: isGroupChannel,
    });

    setIsImagePickerModalOpen(true);
  };

  const attachmentDisplayName = selectedAttachment?.fileName;

  const handleSendFileMessagePress = async () => {
    if (!selectedAttachment || !attachmentDisplayName || !selectedPatientId) {
      return;
    }

    track(MessagesAnalyticsEvent.AttachmentMessageSent, {
      [MessagesAnalyticsKey.ChannelName]: channelDisplayName,
      [MessagesAnalyticsKey.IsGroupChannel]: isGroupChannel,
    });

    setIsAttachmentProcessing(true);

    const documentExternalId = uuid.v1().toString();
    const newFileName = `${documentExternalId}-${attachmentDisplayName}`;

    const file = await convertAssetToFile({
      file: selectedAttachment,
      fileName: encodeURIComponent(newFileName),
    });

    try {
      const { attachmentUploadUrl } = await getUploadURL({
        channelId: channel.url,
        documentExternalId,
        fileName: attachmentDisplayName,
        patientExternalId: selectedPatientId,
      }).unwrap();

      if (!attachmentUploadUrl) {
        throw new Error("Failed to get attachment upload URL");
      }

      await uploadFileMutation({
        file,
        uploadUrl: attachmentUploadUrl,
      }).unwrap();

      setSelectedAttachment(null);
      setIsAttachmentProcessing(false);
      await onPressSendFileMessage({
        fileName: file.name,
        fileUrl: attachmentUploadUrl,
      });
    } catch (error) {
      setIsAttachmentProcessingError(true);
      setIsAttachmentProcessing(false);
      captureException(error);
    }
    resetUploadFile();
  };

  const handleDismissAttachment = () => {
    setSelectedAttachment(null);
    setIsAttachmentProcessingError(false);
    resetUploadFile();
  };

  const renderMentionList = () => {
    if (!mentionQuery || !mentionCandidates) {
      return null;
    }

    return (
      <MentionList
        onSelect={handleMentionSelect}
        testID={ChannelCardTestID.MentionList}
        users={mentionCandidates}
      />
    );
  };

  const inputPlaceholder = isChannelFrozen
    ? "Messaging is unavailable"
    : "Type your message";

  const iconPaddingBottom = (InputFieldBaseStyles.height - IconButtonSize) / 2;
  return (
    <>
      <KeyboardAvoidingView>
        {isChannelFrozen && <FrozenChannelBanner testID={channel.url} />}
        {renderMentionList()}
        <VStack gap={12} padding={12}>
          <HStack alignItems="flex-end" gap={12}>
            <Box paddingBottom={iconPaddingBottom}>
              <IconButton
                iconName={FAIconName.Paperclip}
                isDisabled={isChannelFrozen}
                onPress={handleAttachmentPress}
                testID={joinTestIDs(baseTestID, "attachment")}
              />
            </Box>
            <TextField
              hasDynamicHeight
              isDisabled={isChannelFrozen}
              isMultiline
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              onSelectionChange={handleSelectionChange}
              placeholder={inputPlaceholder}
              testID={joinTestIDs(baseTestID, "input")}
              value={message}
            />
            <Box paddingBottom={iconPaddingBottom}>
              <IconButton
                iconName={FAIconName.PaperPlane}
                isDisabled={shouldDisableSendButton({
                  isLoading: isAttachmentProcessing,
                  message,
                  selectedAttachment,
                })}
                onPress={handleSendMessagePress}
                testID={joinTestIDs(baseTestID, "send")}
              />
            </Box>
          </HStack>
          {selectedAttachment && attachmentDisplayName && (
            <Box width="80%">
              <BaseDocumentCard
                errorLabel="Document upload failed"
                fileName={attachmentDisplayName}
                id={attachmentDisplayName}
                isDismissible={!isAttachmentProcessing}
                isInvalid={isAttachmentProcessingError}
                isLoading={isAttachmentProcessing}
                onDismissPress={handleDismissAttachment}
                testID={baseTestID}
              />
            </Box>
          )}
        </VStack>
      </KeyboardAvoidingView>
      <FilePickerModal
        documentMimeTypes={AcceptedDocumentMimeTypes.PatientDocumentUpload}
        imageCompressionSource={ImageCompressionSource.Chat}
        isOpen={isImagePickerModalOpen}
        onAssetSelected={(asset) => setSelectedAttachment(asset)}
        onDismiss={() => setIsImagePickerModalOpen(false)}
        testID={baseTestID}
      />
    </>
  );
};

export default ChannelInput;
